/**
 * Where a note of a switch stands on a tab with a row per partner (National promotion), and how
 * whoever shows the notes tells the two kinds apart.
 *
 * - A row note is about one partner. Its `field` names the row (`rows[3].marginPct`) and its
 *   `cell` is that row's cell (`L29`).
 * - A tab note holds for every partner of the tab alike: a source the switch reads is not loaded.
 *   It is said once, however many rows the tab has. Its `field` has `*` where a row note has the
 *   number of its row (`rows[*].marginPct`): it names the figure and no row. Its `cell` is `null`:
 *   no one cell shows it.
 *
 * A tab without rows (no country, a country without partners) has neither kind.
 *
 * `rowOfNote` reads a field back: the row it names, or `null` for a field that names none. That is
 * the one question a screen asks before it writes "Row 3, Verco" in front of a note.
 *
 * This file imports no switch and no registry: the switches import it. It is not in `deviations/`,
 * which holds one file per switch and nothing else.
 */
/** What stands in the place of the row's number in the field of a tab note: `rows[*].marginPct`. */
export const EVERY_ROW = '*';
const ROWS = 'rows';
/** `rows[3]` at the start of a field: the number of the row, counted from 0. */
const ROW_FIELD = /^rows\[(\d+)\]/;
/** `rows[*]` at the start of a field. */
const EVERY_ROW_FIELD = `${ROWS}[${EVERY_ROW}]`;
/**
 * The places of one figure of a partner row: `key` is its name in the row of the result, `column`
 * its column on the tab, and `firstRow` the row of the tab that `rows[0]` is.
 */
export function rowPlaces(key, column, firstRow) {
    return {
        fieldOf: (index) => `${ROWS}[${index}].${key}`,
        cellOf: (index) => `${column}${firstRow + index}`,
        everyRow: `${EVERY_ROW_FIELD}.${key}`,
    };
}
/**
 * The partner row a field names, counted from 0 (`rows[3].marginPct` is 3), or `null` for a field
 * that names no row: a figure of the tab as a whole (`cost`), a setting
 * (`settings.tieredRebates.eurPerGbp`), and the field of a tab note (`rows[*].marginPct`).
 *
 * For the `field` of a `SwitchDiagnostic` and of a `Diagnostic` of a National promotion result.
 */
export function rowOfNote(field) {
    if (typeof field !== 'string')
        return null;
    const found = ROW_FIELD.exec(field);
    if (found === null)
        return null;
    const index = Number(found[1]);
    return Number.isSafeInteger(index) ? index : null;
}
/**
 * Is this the field of a tab note: one that holds for every partner row and names none
 * (`rows[*].marginPct`)? `false` for the field of a row note and for every other field.
 */
export function isTabNote(field) {
    if (typeof field !== 'string')
        return false;
    return field === EVERY_ROW_FIELD || field.startsWith(`${EVERY_ROW_FIELD}.`);
}
/**
 * The notes of the rows of a tab with what holds for the whole tab said once.
 *
 * `placed` are the notes of every row, each at its own row, in row order. A note whose code is one
 * of `forTheTab` is the same on every row it shows on. The first of them becomes a tab note, where
 * it stands: `places.everyRow` for its field, no cell, and the words of `tabMessage` when the
 * sentence of a row would not read without its row ("This partner"). The later ones are left out.
 * Every other note stays as it was handed in.
 */
export function withTabNotes(placed, forTheTab, places, tabMessage = (note) => note.message) {
    const found = [];
    for (const note of placed) {
        if (!forTheTab.includes(note.code)) {
            found.push(note);
            continue;
        }
        if (found.some((one) => one.code === note.code))
            continue;
        const cell = null;
        found.push({ code: note.code, field: places.everyRow, cell, message: tabMessage(note) });
    }
    return found;
}
//# sourceMappingURL=row-notes.js.map